import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, getQueryKey, invalidateAfterDelete, saveBlobResponse } from '@feature/utils';

import * as api from './MobilityApi';
import { Mobility, MobilityRequest } from './MobilityTypes';

const mobilityKey = getQueryKey('mobility');

export function getMobilityListOptions(year?: number): UseSuspenseQueryOptions<Mobility[]> {
  return {
    queryKey: mobilityKey.as(['list', year ?? null]),
    queryFn: () => api.list(year),
  };
}

export function getMobilityOptions(mobilityId: Id): UseSuspenseQueryOptions<Mobility> {
  return {
    queryKey: mobilityKey.one(mobilityId),
    queryFn: () => api.get(mobilityId),
  };
}

export function useMobilityCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: MobilityRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: mobilityKey.root(),
      }),
  });
}

export function useMobilityUpdate(mobilityId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: MobilityRequest) => api.update(mobilityId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: mobilityKey.root(),
      }),
  });
}

export function useMobilityDelete(mobilityId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(mobilityId),
    onSuccess: () => invalidateAfterDelete(queryClient, mobilityKey, mobilityId),
  });
}

export function useMobilityCertificateUpload(mobilityId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadCertificate(mobilityId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: mobilityKey.root(),
      }),
  });
}

export function useMobilityCertificateDownload(mobilityId: Id) {
  return useMutation({
    mutationFn: (fallbackFilename: string) =>
      api
        .downloadCertificate(mobilityId)
        .then(response => saveBlobResponse(response, fallbackFilename))
        // A blob request gets its 403/404 error body as a Blob, which the notification would read as a MIME type.
        .catch(() => {
          throw new Error('certificate download failed');
        }),
  });
}

export function useMobilityCertificateDelete(mobilityId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteCertificate(mobilityId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: mobilityKey.root(),
      }),
  });
}
